import { useState } from 'react';
import { Link } from 'react-router-dom';
import { get, post } from '../api';
import { ComposeButtons, type ComposeLink } from '../components/ComposeButtons';
import { describe } from '../errors';
import { useLoad } from '../hooks';

interface DueItem {
  id: number;
  trigger: string;
  trigger_label: string;
  channel: 'email' | 'sms';
  recipient_name: string;
  to: string;
  visit_id: number | null;
  visit_start: string | null;
  /** In the business's own timezone. */
  visit_date: string | null;
  failed: boolean;
  error: string | null;
}

interface Composed {
  id: number;
  channel: 'email' | 'sms';
  to: string;
  subject: string;
  body: string;
  href: string;
}

/**
 * Dashboard: messages set to Manual that are due now. Each opens in the office's
 * own mail or messaging app with everything filled in; the office then says
 * whether it went. Hidden when there is nothing to send.
 */
export function ToSendCard() {
  const [tick, setTick] = useState(0);
  const due = useLoad(() => get<DueItem[]>('/api/messages/due'), [tick]);
  const [open, setOpen] = useState<Composed | null>(null);
  const [error, setError] = useState('');

  const items = Array.isArray(due.data) ? due.data : [];
  if (items.length === 0) return null;

  async function prepare(id: number) {
    setError('');
    try {
      setOpen(await post<Composed>(`/api/messages/${id}/compose`));
    } catch (err) {
      setError(describe(err).problems[0] ?? 'Could not prepare that message.');
      setTick((t) => t + 1);
    }
  }

  async function done(id: number, status: 'sent' | 'skipped') {
    try {
      await post(`/api/messages/${id}/${status === 'sent' ? 'sent' : 'skip'}`);
      setOpen(null);
      setTick((t) => t + 1);
    } catch (err) {
      setError(describe(err).problems[0] ?? 'Could not update that message.');
    }
  }

  return (
    <section className="box-list to-send" aria-labelledby="card-to-send">
      <h2 id="card-to-send">
        <span className="card-title">To send</span>
        <span className="count-badge on">{items.length}</span>
      </h2>
      <p className="card-hint muted">
        Messages set to Manual that are due, and any that did not go automatically
      </p>
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      {items.map((it) => (
        <div key={it.id} className="box-item stack-tight">
          <button
            type="button"
            className="link"
            aria-expanded={open?.id === it.id}
            onClick={() => (open?.id === it.id ? setOpen(null) : void prepare(it.id))}
          >
            <strong>{it.trigger_label}</strong>
            <span className="muted sub">
              {' '}
              · {it.recipient_name} · {it.channel === 'email' ? 'Email' : 'SMS'}
              {it.visit_date ? ` · ${it.visit_date}` : ''}
            </span>
          </button>
          {it.failed && (
            <p className="error compose-note">
              Did not go automatically: {it.error ?? 'unknown reason'}
            </p>
          )}
          {open?.id === it.id && (
            <>
              <ComposeButtons items={[open as ComposeLink]} onDone={(s) => void done(it.id, s)} />
              {it.visit_id && <Link to={`/visits/${it.visit_id}`}>Open the visit</Link>}
            </>
          )}
        </div>
      ))}
    </section>
  );
}
